import React, { useState, useEffect, useRef, useMemo } from "react";
import {
    Autocomplete,
    TextField,
    CircularProgress,
    Box,
    Typography,
    Chip,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import * as ubigeoService from "../../services/ubigeoService";

/**
 * Normaliza cadenas para comparaciones flexibles (sin acentos, en minusculas).
 */
const normalizeText = (str) =>
    (str || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim();

/**
 * Filtra localmente sobre los distritos cargados en memoria.
 * Permite buscar por palabras clave en distrito, provincia, departamento, pais o codigo.
 */
const filterLocalUbigeos = (list, query) => {
    if (!query) return list;
    const q = normalizeText(query);
    if (!q) return list;

    const words = q.split(/\s+/).filter(Boolean);

    return list.filter((item) => {
        const desc = normalizeText(item.descripcion);
        const prov = normalizeText(item.provincia);
        const dep = normalizeText(item.departamento);
        const pais = normalizeText(item.pais);
        const cod = (item.codigo || "").toLowerCase();
        const full = `${desc} ${prov} ${dep} ${pais} ${cod}`;

        return words.every((w) => full.includes(w));
    });
};

/**
 * Componente unificado y optimizado de busqueda de Ubigeo.
 *
 * Regla de optimizacion de base de datos:
 * - La consulta contra la base de datos se ejecuta UNICAMENTE cuando la cantidad
 *   de caracteres pasa de 2 a 3 (ej: al escribir "tac").
 * - A partir de ese momento, al continuar escribiendo (4, 5 o mas caracteres),
 *   la busqueda se realiza dentro de los resultados cargados en memoria,
 *   evitando multiples consultas innecesarias a la base de datos.
 * - Si el usuario cambia el prefijo raiz de 3 caracteres, se vuelve a consultar la BD.
 */
const UbigeoAutocomplete = ({
    value,
    initialLabel = "",
    onChange,
    label = "Ubigeo / Distrito",
    name = "ubigeo",
    placeholder = "Distrito, provincia o código...",
    disabled = false,
    required = false,
    size = "small",
    fullWidth = true,
    error = false,
    helperText = "",
    sx = {},
}) => {
    const [selectedOption, setSelectedOption] = useState(null);
    const [inputValue, setInputValue] = useState("");
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);

    // Cache en memoria para resultados cargados por el prefijo de 3 caracteres
    const cacheRef = useRef({ prefix: "", results: [] });
    const textoRef = useRef("");
    const abortControllerRef = useRef(null);
    const debounceTimerRef = useRef(null);

    // Sincronizar valor inicial si viene como objeto, id o label
    useEffect(() => {
        if (!value && !initialLabel) {
            setSelectedOption(null);
            setInputValue("");
            return;
        }

        if (typeof value === "object" && value !== null) {
            setSelectedOption(value);
            setInputValue(value.descripcion || "");
            return;
        }

        // Si viene un ID numerico o codigo string y tenemos el label descriptivo
        if (initialLabel) {
            const fallbackOption = {
                id: typeof value === "number" ? value : 0,
                codigo: typeof value === "string" ? value : String(value || ""),
                descripcion: initialLabel,
                provincia: "",
                departamento: "",
                pais: "",
            };
            setSelectedOption(fallbackOption);
            setInputValue(initialLabel);
            return;
        }

        // Si solo viene un ID numerico > 0, cargamos sus datos del backend
        if (typeof value === "number" && value > 0) {
            let active = true;
            ubigeoService.getUbigeoById(value)
                .then((data) => {
                    if (active && data) {
                        setSelectedOption(data);
                        setInputValue(data.descripcion || "");
                    }
                })
                .catch(() => {});
            return () => { active = false; };
        }

        // Si solo viene un codigo string
        if (typeof value === "string" && value.trim().length >= 3) {
            let active = true;
            ubigeoService.getUbigeoByCodigo(value.trim())
                .then((data) => {
                    if (active && data) {
                        setSelectedOption(data);
                        setInputValue(data.descripcion || "");
                    }
                })
                .catch(() => {});
            return () => { active = false; };
        }
    }, [value, initialLabel]);

    // Manejo de cambios en el texto con cache en memoria y busqueda a BD solo al pasar a 3 caracteres
    const handleInputChange = (event, newInputValue, reason) => {
        setInputValue(newInputValue);
        textoRef.current = newInputValue || "";

        if (reason === "reset") {
            return;
        }

        if (reason === "clear") {
            cacheRef.current = { prefix: "", results: [] };
            setOptions([]);
            setLoading(false);
            if (onChange) onChange(null);
            return;
        }

        const texto = (newInputValue || "").trim();

        // 1. Menos de 3 caracteres: limpiar opciones y cache; NO consultar la BD
        if (texto.length < 3) {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
                debounceTimerRef.current = null;
            }
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
                abortControllerRef.current = null;
            }
            cacheRef.current = { prefix: "", results: [] };
            setOptions([]);
            setLoading(false);
            return;
        }

        const currentPrefix = texto.slice(0, 3).toLowerCase();

        // 2. Si ya tenemos en memoria los resultados cargados para este prefijo de 3 caracteres,
        // NO se consulta la BD: se busca directamente dentro de los resultados cargados.
        if (cacheRef.current.prefix === currentPrefix && cacheRef.current.results.length > 0) {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
                debounceTimerRef.current = null;
            }
            const filtered = filterLocalUbigeos(cacheRef.current.results, texto);
            setOptions(filtered.slice(0, 20));
            setLoading(false);
            return;
        }

        // 3. El usuario paso de 2 a 3 caracteres (o cambio el prefijo raiz de 3 letras):
        // Se cancelan peticiones anteriores y se consulta la base de datos con debounce de 350ms.
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }

        setLoading(true);

        debounceTimerRef.current = setTimeout(async () => {
            const controller = new AbortController();
            abortControllerRef.current = controller;

            try {
                // Se consulta a la BD con el prefijo raiz solicitando hasta 100 distritos para el cache local
                const results = await ubigeoService.buscarUbigeos(currentPrefix, controller.signal, 100);
                const loaded = Array.isArray(results) ? results : [];

                // Almacenar en cache los resultados para este prefijo
                cacheRef.current = { prefix: currentPrefix, results: loaded };

                // Filtrar con el texto completo actual que el usuario haya escrito
                const currentText = (textoRef.current || texto).trim();
                const filtered = filterLocalUbigeos(loaded, currentText);
                setOptions(filtered.slice(0, 20));
            } catch (err) {
                if (err?.name !== "CanceledError" && err?.name !== "AbortError" && err?.code !== "ERR_CANCELED") {
                    setOptions([]);
                }
            } finally {
                setLoading(false);
            }
        }, 350);
    };

    // Al seleccionar una opcion
    const handleSelectOption = (event, newValue) => {
        setSelectedOption(newValue);
        if (onChange) {
            onChange(newValue);
        }
    };

    // Asegurar que la opcion seleccionada forme parte de la lista para MUI Autocomplete
    const mergedOptions = useMemo(() => {
        if (!selectedOption) return options;
        const exists = options.some(
            (o) => o.id === selectedOption.id || (o.codigo && o.codigo === selectedOption.codigo)
        );
        return exists ? options : [selectedOption, ...options];
    }, [selectedOption, options]);

    return (
        <Autocomplete
            fullWidth={fullWidth}
            size={size}
            disabled={disabled}
            options={mergedOptions}
            value={selectedOption}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            onChange={handleSelectOption}
            isOptionEqualToValue={(option, val) => {
                if (!option || !val) return false;
                if (option.id && val.id) return option.id === val.id;
                if (option.codigo && val.codigo) return option.codigo === val.codigo;
                return option.descripcion === val.descripcion;
            }}
            getOptionLabel={(option) => {
                if (!option) return "";
                if (typeof option === "string") return option;
                return option.descripcion || option.codigo || "";
            }}
            filterOptions={(x) => x} // El filtrado ya lo realiza la logica de cache local optimizada
            noOptionsText={
                (inputValue || "").trim().length < 3
                    ? "Escriba al menos 3 caracteres..."
                    : "No se encontraron distritos"
            }
            loading={loading}
            loadingText="Buscando distritos..."
            renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                const jerarquia = [option.provincia, option.departamento, option.pais]
                    .filter(Boolean)
                    .join(" · ");

                return (
                    <li key={option.id || option.codigo || key} {...otherProps} style={{ padding: "6px 12px" }}>
                        <Box sx={{ display: "flex", alignItems: "center", width: "100%", gap: 1 }}>
                            <LocationOnIcon fontSize="small" color="action" sx={{ mr: 0.5, flexShrink: 0 }} />
                            <Box sx={{ flexGrow: 1, overflow: "hidden" }}>
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, color: "text.primary", lineHeight: 1.2 }}
                                    noWrap
                                >
                                    {option.descripcion}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{ color: "text.secondary", lineHeight: 1.1 }}
                                    noWrap
                                >
                                    {jerarquia || "Perú"}
                                </Typography>
                            </Box>
                            {option.codigo && (
                                <Chip
                                    label={option.codigo}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                        fontSize: "0.7rem",
                                        height: 20,
                                        color: "text.secondary",
                                        borderColor: "divider",
                                        flexShrink: 0,
                                    }}
                                />
                            )}
                        </Box>
                    </li>
                );
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    name={name}
                    required={required}
                    error={error}
                    helperText={helperText}
                    placeholder={placeholder}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
            sx={sx}
        />
    );
};

export default UbigeoAutocomplete;
